import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Chip, Tooltip } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { Content } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

import { RepositoryEntityV1alpha1 } from '../../../../../../../../backend/src/custom-processors/types';
import { PRImpactTable } from '../../../../../catalog/group/team/tabs/health/PRImpactTable';
import { HealthLoadingState } from '../../../../../catalog/group/team/tabs/health/HealthLoadingState';
import { HealthNoDataState } from '../../../../../catalog/group/team/tabs/health/HealthNoDataState';
import { DateRangePicker, DateRange, parseDateRangeFromParams } from '../../../../../catalog/group/team/tabs/health/DateRangePicker';
import {
  teamMetricsApiRef,
  UIPRWithTypedCoverage,
  UITestTypeCoverage,
  UICoverageByType,
  getStatus,
} from '../../../../../../api/teamMetricsApi';
import { calculateWeeklyPRData, calculatePRSummary } from '../../../../../catalog/group/team/tabs/health/utils';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4),
  },
  titleSection: {},
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1A1A1A',
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    fontSize: 14,
    color: '#8E9299',
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  infoIcon: {
    fontSize: 14,
    color: '#CBD5E0',
    cursor: 'help',
  },
  chip: {
    fontSize: 10,
    fontWeight: 700,
    height: 20,
    marginLeft: 'auto',
  },
  chipAbove: {
    backgroundColor: '#F0FFF4',
    color: '#22543D',
    border: '1px solid #C6F6D5',
  },
  chipBelow: {
    backgroundColor: '#FFFAF0',
    color: '#744210',
    border: '1px solid #FEEBC8',
  },
  tooltipContent: {
    padding: 8,
  },
  tooltipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  tooltipDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  tooltipLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#E2E8F0',
    minWidth: 70,
  },
  tooltipValue: {
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    fontFamily: 'monospace',
  },
  card: {
    padding: theme.spacing(3),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  cardLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: theme.spacing(1),
  },
  cardValueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
  cardValue: {
    fontSize: 36,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  cardValueHealthy: {
    fontSize: 36,
    fontWeight: 700,
    color: '#38A169',
  },
  cardValueWarning: {
    fontSize: 36,
    fontWeight: 700,
    color: '#D69E2E',
  },
  cardValueCritical: {
    fontSize: 36,
    fontWeight: 700,
    color: '#E53E3E',
  },
  cardTrend: {
    fontSize: 12,
    fontWeight: 700,
  },
  cardTrendPositive: {
    color: '#38A169',
  },
  cardTrendNegative: {
    color: '#E53E3E',
  },
  cardSubtext: {
    fontSize: 10,
    color: '#8E9299',
    marginTop: theme.spacing(0.5),
  },
  divider: {
    borderTop: '1px solid #E2E8F0',
    margin: theme.spacing(4, 0),
  },
  chartSection: {
    marginBottom: theme.spacing(4),
  },
  chartHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  chartTitle: {
    fontSize: 20,
    fontWeight: 700,
  },
  chartContainer: {
    height: 200,
    padding: theme.spacing(2),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  chartWrapper: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  chartArea: {
    flex: 1,
    height: 180,
  },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    paddingRight: theme.spacing(2),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
  legendLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#4A5568',
  },
  legendValue: {
    fontSize: 12,
    fontWeight: 700,
    color: '#1A1A1A',
    marginLeft: 'auto',
  },
  prSection: {
    marginBottom: theme.spacing(4),
  },
  prHeader: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: theme.spacing(3),
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: '#EBF8FF',
    border: '1px solid #BEE3F8',
    borderRadius: 4,
  },
  infoBannerIcon: {
    fontSize: 16,
    color: '#3182CE',
    marginTop: 2,
  },
  infoText: {
    fontSize: 12,
    color: '#2B6CB0',
    lineHeight: 1.5,
  },
}));

interface RepoSummary {
  coverage: number | null;
  trend: string | null;
  orgAverage: number | null;
  deploys: number;
  totalUnits: number | null;
  prCount: number;
}

interface RepoState {
  loading: boolean;
  error: Error | null;
  summary: RepoSummary | null;
  coverageByType: UITestTypeCoverage[];
  orgCoverageByType: UICoverageByType[];
  prs: UIPRWithTypedCoverage[];
}

const HealthContent: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity<RepositoryEntityV1alpha1>();
  const teamMetricsApi = useApi(teamMetricsApiRef);

  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    parseDateRangeFromParams(new URLSearchParams(window.location.search))
  );
  const [showPRDrilldown, setShowPRDrilldown] = useState(false);

  const repoName = entity?.metadata?.name || 'unknown';

  const [repoState, setRepoState] = useState<RepoState>({
    loading: true,
    error: null,
    summary: null,
    coverageByType: [],
    orgCoverageByType: [],
    prs: [],
  });

  useEffect(() => {
    let cancelled = false;

    const fetchRepoReport = async () => {
      setRepoState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await teamMetricsApi.GenerateRepoReport({
          repoName,
          startDate: dateRange.start,
        });

        if (!cancelled) {
          const combinedCoverage = response.coverageByType.find(
            (c) => c.testType === 'go_combined'
          );
          const currentCoverage = combinedCoverage?.coveragePercentage ?? null;
          const coverageDelta = combinedCoverage?.coverageDelta ?? null;

          const orgCombined = response.orgCoverageByType.find(
            (c) => c.testType === 'go_combined'
          );
          const orgAverage = orgCombined?.averageCoverage ?? null;

          const formatTrend = (delta: number | null): string | null => {
            if (delta === null) return null;
            const formatted = delta.toFixed(2);
            return delta >= 0 ? `+${formatted}%` : `${formatted}%`;
          };

          setRepoState({
            loading: false,
            error: null,
            summary: {
              coverage: currentCoverage,
              trend: formatTrend(coverageDelta),
              orgAverage,
              deploys: response.deploymentCount,
              totalUnits: response.totalUnits,
              prCount: response.prs.length,
            },
            coverageByType: response.coverageByType,
            orgCoverageByType: response.orgCoverageByType,
            prs: response.prs,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setRepoState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err : new Error('Failed to fetch repo health data'),
          }));
        }
      }
    };

    if (repoName && repoName !== 'unknown') {
      fetchRepoReport();
    }

    return () => {
      cancelled = true;
    };
  }, [repoName, dateRange.start, teamMetricsApi]);

  const handleAuthorClick = (weaveEmail: string) => {
    navigate(`/user-health/${encodeURIComponent(weaveEmail)}`);
  };

  const weeklyPRData = useMemo(
    () => calculateWeeklyPRData(repoState.prs, dateRange.start),
    [repoState.prs, dateRange.start],
  );

  const prSummary = useMemo(
    () => calculatePRSummary(repoState.prs),
    [repoState.prs],
  );

  const TEST_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
    go_combined: { label: 'Combined', color: '#805AD5' },
    go_unit: { label: 'Unit', color: '#3182CE' },
    go_tas: { label: 'TAS', color: '#38A169' },
  };

  const getCoverageByTestType = (coverages: UITestTypeCoverage[] | UICoverageByType[] | undefined, testType: string) => {
    return coverages?.find((c) => c.testType === testType);
  };

  const formatDelta = (delta: number) => {
    const formatted = Math.abs(delta).toFixed(1);
    return delta >= 0 ? `+${formatted}%` : `-${formatted}%`;
  };

  const renderRepoCoverageTooltip = (showDelta: boolean = false) => (
    <Box className={classes.tooltipContent}>
      {['go_combined', 'go_unit', 'go_tas'].map((testType) => {
        const coverage = getCoverageByTestType(repoState.coverageByType, testType) as UITestTypeCoverage | undefined;
        const config = TEST_TYPE_CONFIG[testType];
        const value = coverage?.coveragePercentage ?? 0;
        const delta = coverage?.coverageDelta ?? 0;

        return (
          <Box key={testType} className={classes.tooltipRow}>
            <Box className={classes.tooltipDot} style={{ backgroundColor: config.color }} />
            <Typography className={classes.tooltipLabel}>{config.label}:</Typography>
            <Typography className={classes.tooltipValue}>
              {value.toFixed(1)}%
              {showDelta && delta !== 0 && (
                <span style={{ marginLeft: 8, color: delta >= 0 ? '#9AE6B4' : '#FEB2B2' }}>
                  {formatDelta(delta)}
                </span>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  const renderOrgCoverageTooltip = () => (
    <Box className={classes.tooltipContent}>
      {['go_combined', 'go_unit', 'go_tas'].map((testType) => {
        const coverage = getCoverageByTestType(repoState.orgCoverageByType, testType) as UICoverageByType | undefined;
        const config = TEST_TYPE_CONFIG[testType];
        const value = coverage?.averageCoverage ?? 0;

        return (
          <Box key={testType} className={classes.tooltipRow}>
            <Box className={classes.tooltipDot} style={{ backgroundColor: config.color }} />
            <Typography className={classes.tooltipLabel}>{config.label}:</Typography>
            <Typography className={classes.tooltipValue}>
              {value.toFixed(1)}%
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  if (repoState.loading) {
    return <HealthLoadingState />;
  }

  if (repoState.error || !repoState.summary) {
    return <HealthNoDataState context="repo" entityName={repoName} />;
  }

  if (showPRDrilldown) {
    return (
      <Content className={classes.root}>
        <PRImpactTable
          typedPrs={repoState.prs}
          title={`${repoName} PR History`}
          subtitle="Quality impact analysis for repository contributions"
          onBack={() => setShowPRDrilldown(false)}
          onAuthorClick={handleAuthorClick}
        />
      </Content>
    );
  }

  const getCoverageClass = () => {
    const status = getStatus(repoState.summary?.coverage);
    switch (status) {
      case 'healthy':
        return classes.cardValueHealthy;
      case 'warning':
        return classes.cardValueWarning;
      case 'critical':
        return classes.cardValueCritical;
      default:
        return classes.cardValue;
    }
  };

  return (
    <Content className={classes.root}>
      <Box padding={4}>
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.titleSection}>
            <Typography className={classes.title}>Health Metrics</Typography>
            <Typography className={classes.subtitle}>
              Code coverage and quality analysis for {repoName}
            </Typography>
          </Box>

          <DateRangePicker dateRange={dateRange} onChange={setDateRange} />
        </Box>

        {/* Summary Cards */}
        <Box className={classes.summaryCards}>
          {/* Card 1: Combined Coverage */}
          <Paper className={classes.card} elevation={0}>
            <Box className={classes.labelRow}>
              <Typography className={classes.cardLabel}>Combined Coverage</Typography>
              <Tooltip title="Combined code coverage from unit tests and TAS. The delta shows change since the start of the selected time period." arrow placement="top">
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Tooltip>
            </Box>
            <Tooltip title={renderRepoCoverageTooltip(true)} arrow placement="top">
              <Box className={classes.cardValueRow} style={{ cursor: 'help' }}>
                <Typography className={getCoverageClass()}>
                  {repoState.summary!.coverage !== null ? `${repoState.summary!.coverage.toFixed(1)}%` : 'N/A'}
                </Typography>
                {repoState.summary!.trend !== null && (
                  <Typography
                    className={`${classes.cardTrend} ${
                      repoState.summary!.trend.startsWith('+')
                        ? classes.cardTrendPositive
                        : classes.cardTrendNegative
                    }`}
                  >
                    {repoState.summary!.trend}
                  </Typography>
                )}
              </Box>
            </Tooltip>
            <Typography className={classes.cardSubtext}>
              {repoState.summary!.trend?.startsWith('+') ? 'Increased' : repoState.summary!.trend?.startsWith('-') ? 'Decreased' : 'No change'} since start of period
            </Typography>
          </Paper>

          {/* Card 2: vs Org Average */}
          <Paper className={classes.card} elevation={0}>
            <Box className={classes.labelRow} style={{ flexWrap: 'nowrap' }}>
              <Typography className={classes.cardLabel} style={{ whiteSpace: 'nowrap' }}>vs. Org Average</Typography>
              <Tooltip title="Average coverage across all non-excluded Weave repositories with coverage data." arrow placement="top">
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Tooltip>
              {repoState.summary!.coverage !== null && repoState.summary!.orgAverage !== null && (
                <Chip
                  label={`${Math.abs(repoState.summary!.coverage - repoState.summary!.orgAverage).toFixed(1)}% ${repoState.summary!.coverage >= repoState.summary!.orgAverage ? 'ABOVE' : 'BELOW'}`}
                  size="small"
                  className={`${classes.chip} ${repoState.summary!.coverage >= repoState.summary!.orgAverage ? classes.chipAbove : classes.chipBelow}`}
                />
              )}
            </Box>
            <Tooltip title={renderOrgCoverageTooltip()} arrow placement="top">
              <Box className={classes.cardValueRow} style={{ cursor: 'help' }}>
                <Typography className={classes.cardValue} style={{ color: '#4A5568' }}>
                  {repoState.summary!.orgAverage !== null ? `${repoState.summary!.orgAverage.toFixed(1)}%` : 'N/A'}
                </Typography>
              </Box>
            </Tooltip>
            <Typography className={classes.cardSubtext}>Organization-wide average</Typography>
          </Paper>

          {/* Card 3: Activity */}
          <Paper className={classes.card} elevation={0}>
            <Box className={classes.labelRow}>
              <Typography className={classes.cardLabel}>Activity</Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Box style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <Typography style={{ fontSize: 28, fontWeight: 700, color: '#1A1A1A' }}>{repoState.summary!.prCount}</Typography>
                <Typography style={{ fontSize: 12, fontWeight: 500, color: '#718096' }}>PRs</Typography>
              </Box>
              <Box style={{ width: 1, height: 32, backgroundColor: '#E2E8F0' }} />
              <Box style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <Typography style={{ fontSize: 28, fontWeight: 700, color: '#1A1A1A' }}>{repoState.summary!.deploys}</Typography>
                <Typography style={{ fontSize: 12, fontWeight: 500, color: '#718096' }}>Deploys</Typography>
              </Box>
            </Box>
            <Typography className={classes.cardSubtext}>During selected period</Typography>
          </Paper>

          {/* Card 4: Size */}
          <Paper className={classes.card} elevation={0}>
            <Box className={classes.labelRow}>
              <Typography className={classes.cardLabel}>Size</Typography>
              <Tooltip title="For Go repositories, this is the number of statements in the codebase. Other languages may use different metrics (e.g., lines of code)." arrow placement="top">
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Tooltip>
            </Box>
            <Box className={classes.cardValueRow}>
              <Typography className={classes.cardValue}>
                {repoState.summary!.totalUnits !== null ? repoState.summary!.totalUnits.toLocaleString() : 'N/A'}
              </Typography>
            </Box>
            <Typography className={classes.cardSubtext}>Total statements</Typography>
          </Paper>
        </Box>

        <Box className={classes.divider} />

        {/* PR Activity Chart */}
        <Box className={classes.chartSection}>
          <Box className={classes.chartHeader}>
            <Typography className={classes.chartTitle}>PR Activity</Typography>
          </Box>
          <Paper className={classes.chartContainer} elevation={0}>
            <Box className={classes.chartWrapper}>
              <Box className={classes.chartArea}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyPRData} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis
                      dataKey="week"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: '#8E9299' }}
                      interval={Math.max(0, Math.floor(weeklyPRData.length / 8))}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: '#8E9299' }}
                      allowDecimals={false}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 4,
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        fontSize: 12,
                      }}
                      formatter={(value: number, name: string) => {
                        const labels: Record<string, string> = {
                          improved: 'Improved Coverage',
                          decreased: 'Decreased Coverage',
                          neutral: 'No Change',
                        };
                        return [`${value} PRs`, labels[name] || name];
                      }}
                    />
                    <Bar dataKey="improved" stackId="prs" fill="#68D391" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="neutral" stackId="prs" fill="#CBD5E0" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="decreased" stackId="prs" fill="#FC8181" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <Box className={classes.legend}>
                <Box className={classes.legendItem}>
                  <Box className={classes.legendDot} style={{ backgroundColor: '#68D391' }} />
                  <Typography className={classes.legendLabel}>Improved Coverage</Typography>
                  <Typography className={classes.legendValue}>{prSummary.improved}</Typography>
                </Box>
                <Box className={classes.legendItem}>
                  <Box className={classes.legendDot} style={{ backgroundColor: '#CBD5E0' }} />
                  <Typography className={classes.legendLabel}>No Change</Typography>
                  <Typography className={classes.legendValue}>{prSummary.neutral}</Typography>
                </Box>
                <Box className={classes.legendItem}>
                  <Box className={classes.legendDot} style={{ backgroundColor: '#FC8181' }} />
                  <Typography className={classes.legendLabel}>Decreased Coverage</Typography>
                  <Typography className={classes.legendValue}>{prSummary.decreased}</Typography>
                </Box>
                <Box className={classes.divider} style={{ margin: '8px 0' }} />
                <Box className={classes.legendItem}>
                  <Typography className={classes.legendLabel} style={{ fontWeight: 700 }}>Total PRs</Typography>
                  <Typography className={classes.legendValue}>{prSummary.total}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* PR Impact Section */}
        <Box className={classes.prSection}>
          <Typography className={classes.prHeader}>Recent PR Activity</Typography>
          <PRImpactTable
            typedPrs={repoState.prs}
            title=""
            subtitle=""
            onAuthorClick={handleAuthorClick}
          />
        </Box>

        {/* Footer Info */}
        <Box className={classes.infoBox}>
          <InfoOutlinedIcon className={classes.infoBannerIcon} />
          <Typography className={classes.infoText}>
            <strong>Repository Metrics:</strong> This dashboard shows metrics specific to
            this repository. Coverage data is tracked per Pull Request and aggregated over time.
          </Typography>
        </Box>
      </Box>
    </Content>
  );
};

export default HealthContent;
